/**
 * Search utilities for finding and matching words in captions
 */

/**
 * Escape special regex characters in a string
 */
export const escapeRegex = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Create a RegExp from search query based on options
 */
export const createSearchRegex = (query, options = {}) => {
  const { matchCase = false, useRegex = false, wholeWord = false } = options;
  
  if (!query) return null;
  
  try {
    let pattern = useRegex ? query : escapeRegex(query);
    
    if (wholeWord) {
      // Word boundaries - works for both Arabic and Latin
      pattern = `(?:^|\\s|[\\u0600-\\u06FF](?=[^\\u0600-\\u06FF])|[^\\u0600-\\u06FF](?=[\\u0600-\\u06FF]))${pattern}(?:$|\\s|[\\u0600-\\u06FF](?=[^\\u0600-\\u06FF])|[^\\u0600-\\u06FF](?=[\\u0600-\\u06FF]))`;
    }
    
    const flags = matchCase ? 'g' : 'gi';
    return new RegExp(pattern, flags);
  } catch (e) {
    // Invalid regex
    console.error('Invalid search regex:', e);
    return null;
  }
};

/**
 * Check if a word matches the search query
 */
export const matchesSearch = (word, query, options = {}) => {
  if (!query || !word) return false;
  
  const { matchCase = false, useRegex = false, wholeWord = false, searchField = 'both' } = options;
  
  // Get text to search
  let textsToSearch = [];
  if (searchField === 'arabic' || searchField === 'both') {
    if (word.text) textsToSearch.push(word.text);
  }
  if (searchField === 'arabizi' || searchField === 'both') {
    if (word.arabizi) textsToSearch.push(word.arabizi);
  }
  
  if (textsToSearch.length === 0) return false;
  
  const regex = createSearchRegex(query, { matchCase, useRegex, wholeWord });
  if (!regex) return false;
  
  return textsToSearch.some(text => regex.test(text));
};

/**
 * Find all matching words in caption data
 */
export const findMatchingWords = (captionData, query, options = {}) => {
  if (!query || !captionData?.words) return [];
  
  const { 
    matchCase = false, 
    useRegex = false, 
    wholeWord = false,
    searchField = 'both',
    highlightColor = null,
    importantOnly = false,
    timeRange = null // { start: number, end: number }
  } = options;
  
  const matches = [];
  
  captionData.words.forEach((word) => {
    // Skip silence and punctuation
    if (word.type === 'silence' || word.type === 'punctuation') return;
    
    // Apply filters
    if (importantOnly && !word.important) return;
    if (highlightColor && word.highlightColor !== highlightColor) return;
    if (timeRange) {
      if (word.start_time < timeRange.start || word.end_time > timeRange.end) return;
    }
    
    // Check if word matches
    if (matchesSearch(word, query, { matchCase, useRegex, wholeWord, searchField })) {
      // Find which skeleton this word belongs to
      let skeletonIndex = -1;
      for (let i = 0; i < captionData.skeleton.length; i++) {
        if (captionData.skeleton[i].words.includes(word.id)) {
          skeletonIndex = i;
          break;
        }
      }
      
      matches.push({
        wordId: word.id,
        word,
        skeletonIndex,
        matchText: options.searchField === 'arabizi' ? (word.arabizi || word.text) : word.text
      });
    }
  });
  
  return matches;
};

/**
 * Replace text in a word
 */
export const replaceWordText = (word, searchQuery, replaceText, options = {}) => {
  const { matchCase = false, useRegex = false, searchField = 'both' } = options;
  
  const regex = createSearchRegex(searchQuery, { matchCase, useRegex, wholeWord: false });
  if (!regex) return word;
  
  const newWord = { ...word };
  
  if (searchField === 'arabic' || searchField === 'both') {
    if (word.text) {
      newWord.text = word.text.replace(regex, replaceText);
    }
  }
  
  if (searchField === 'arabizi' || searchField === 'both') {
    if (word.arabizi) {
      newWord.arabizi = word.arabizi.replace(regex, replaceText);
    }
  }
  
  return newWord;
};

/**
 * Get context around a word (previous and next words)
 */
export const getWordContext = (captionData, wordId, contextSize = 2) => {
  const allWords = captionData.words.filter(w => w.type !== 'silence');
  const index = allWords.findIndex(w => w.id === wordId);
  
  if (index === -1) return { before: [], after: [] };
  
  const before = allWords.slice(Math.max(0, index - contextSize), index);
  const after = allWords.slice(index + 1, index + 1 + contextSize);
  
  return { before, after };
};
